import React, { createContext, useContext, useEffect, useState } from 'react';
import { getSymbols } from '../../../utils/api';
import { getOptionData } from '../ApiCalls/FetchExpirySymbols';
import WebsocketWrapperContext from '../../../context/WebsocketWrapperContext';
import { getAllOptionCodesFromAllOptionsData } from '../helpers/GetAllCodes';
import { useBuilderContext } from '../../StrategyCreator/context/BuilderContext';

const OptionChainScreenContext = createContext(null);
export const useOptionChainScreenContext = () => useContext(OptionChainScreenContext);

export const OptionChainScreenProvider = ({ children }) => {
    const {
        checkWebsocketConnection,
        subscribeToSymbols,
        unsubscribeToSymbols,
        symbolList,
        symbolData,
    } = useContext(WebsocketWrapperContext);

    const {
        allSymbols, setAllSymbols, allOptionsData, setAlloptionsData
    } = useBuilderContext();

    const [selectedSymbol, setSelectedSymbol] = useState(null);
    const [searchSymbol, setSearchSymbol] = useState(null);
    const [currentOptionsData, setCurrentOptionsData] = useState({});
    const [rows, setRows] = useState({});
    const [selectedData, setSelectedData] = useState([]);
    const [selectedExpiry, setSelectedExpiry] = useState('');
    const [symbolsActiveCategory, setSymbolsActiveCategory] = useState({});
    const [symbolsSelectedStrategy, setSymbolsSelectedStrategy] = useState({});
    const [subscribedOptionCodes, setSubscribedOptionCodes] = useState([]);
    const [limitOrderStates, setLimitOrderStates] = useState({})
    const [lotx, setLotx] = useState(1);
    const [lotxMap, setLotxMap] = useState({});
    const [marketType, setMarketType] = useState("NRML");
    const [marketTypeMap, setMarketTypeMap] = useState({});
    const [activeTab, setActiveTab] = useState('Nifty');
    const [configureSettingsMap, setConfigureSettingsMap] = useState(() => {
        const saved = localStorage.getItem("configureSettingsMap");
        return saved
            ? JSON.parse(saved)
            : {
                showCalls: true,
                showPuts: true,
                showDelta: false,
                showTheta: false,
                showGamma: false,
                showVega: false,
                showOIChng: false,
                showOIBuilddup: false,
                noOfStrikes: 30,
            };
    });


    useEffect(() => {
        localStorage.setItem(
            "configureSettingsMap",
            JSON.stringify(configureSettingsMap)
        );
    }, [configureSettingsMap]);


    useEffect(() => {
        if (!currentOptionsData[selectedExpiry]) return;
        const optionsLength = currentOptionsData[selectedExpiry]?.options?.length || 0;

        setConfigureSettingsMap((prev) => ({
            ...prev,
            maxStrikes: optionsLength,
        }));
    }, [currentOptionsData[selectedExpiry]])

    useEffect(() => {
        const currentLotx = selectedSymbol ? (lotxMap[selectedSymbol.scrip] ?? 1) : 1;
        setLotx(currentLotx)
    }, [selectedSymbol, lotxMap])

    useEffect(() => {
        const currentMarketType = selectedSymbol ? (marketTypeMap[selectedSymbol.scrip] ?? "NRML") : "NRML";
        setMarketType(currentMarketType)
    }, [selectedSymbol, marketTypeMap])

    useEffect(() => {
        if (!selectedSymbol) return;

        subscribeToSymbols([selectedSymbol.code], selectedSymbol.exchange || "NSE");

        setSelectedExpiry(selectedSymbol?.option_expiries?.[0]);

        const existingData = allOptionsData[selectedSymbol?.scrip];
        if (existingData) {
            return;
        }

        const fetchAllOptions = async () => {
            if (!selectedSymbol?.option_expiries || !selectedSymbol.scrip) return;

            const expiryDates = selectedSymbol.option_expiries;
            const requests = expiryDates.map(async (date) => {
                const formattedDate = date.replaceAll(" ", "%20");
                // console.log("here",selectedSymbol.scrip + formattedDate);
                const data = await getOptionData(selectedSymbol.scrip, formattedDate);
                return { [date]: data };
            });

            const results = await Promise.all(requests);
            const mergedData = Object.assign({}, ...results);
            setAlloptionsData((prev) => ({
                ...prev,
                [selectedSymbol.scrip]: mergedData,
            }));
            // const codes = getAllOptionCodesFromAllOptionsData(mergedData, selectedSymbol?.option_expiries?.[0]);
            // updateOptionSubscriptions(codes);
        };

        fetchAllOptions();
    }, [selectedSymbol]);

    //subscribing codes

    // const updateOptionSubscriptions = (optionCodes) => {
    //     if (subscribedOptionCodes.length > 0) {
    //         unsubscribeToSymbols(subscribedOptionCodes, selectedSymbol.exchange);
    //     }
    //     if (optionCodes.length > 0) {
    //         subscribeToSymbols(optionCodes, selectedSymbol.exchange);
    //         setSubscribedOptionCodes(optionCodes);
    //     }
    // };

    // useEffect(() => {
    //     const existingData = allOptionsData[selectedSymbol?.scrip];
    //     if (currentOptionsData) {
    //         const codes = getAllOptionCodesFromAllOptionsData(currentOptionsData, selectedExpiry);
    //         updateOptionSubscriptions(codes);
    //         // console.log(codes, 'codes')
    //     }
    // }, [currentOptionsData])

    //updating current options data
    useEffect(() => {
        if (!selectedSymbol || !allOptionsData[selectedSymbol?.scrip]) return;
        setCurrentOptionsData(allOptionsData[selectedSymbol?.scrip]);
    }, [selectedSymbol, allOptionsData])

    //store all symbols and set initial symbol
    useEffect(() => {
        const fetchSymbols = async () => {
            const symbols = await getSymbols();
            if (!symbols) return;

            // Clear potentially corrupted session storage
            const storedSymbol = sessionStorage.getItem("selectedSymbol");
            // console.log('🔍 OptionChainScreen: Found stored symbol in session storage:', storedSymbol);
            let validSymbol = null;

            if (storedSymbol) {
                try {
                    const parsedSymbol = JSON.parse(storedSymbol);
                    // Verify the stored symbol is valid
                    const isValidSymbol = symbols.find(s => s.scrip === parsedSymbol.scrip);
                    if (isValidSymbol) {
                        validSymbol = parsedSymbol;
                    } else {
                        // Clear invalid session storage
                        sessionStorage.removeItem("selectedSymbol");
                    }
                } catch (error) {
                    // Clear corrupted session storage
                    sessionStorage.removeItem("selectedSymbol");
                }
            }

            // For now, let's always start with NIFTY to ensure consistency
            const niftySymbol = symbols.find(
                (symbol) => symbol.scrip === "NIFTY"
            );
            // console.log('🔍 OptionChainScreen: Always starting with NIFTY symbol for consistency');
            setSelectedSymbol(niftySymbol);
            setActiveTab('Nifty');
            // Clear any potentially corrupted session storage
            sessionStorage.removeItem("selectedSymbol");
            setAllSymbols(symbols);
        };
        fetchSymbols();
    }, []);

    //basket logic

    // Sync selectedData with rows
    useEffect(() => {
        const currentRows = rows[selectedSymbol?.scrip] || [];
        setSelectedData(currentRows);
    }, [rows, selectedSymbol]);

    // Update prices from websocket for Market orders only (initial fill).
    // Do not rewrite on every tick, otherwise selectedData keeps changing and
    // downstream API effects (margin/charges) can fire continuously.
    useEffect(() => {
        const currentRows = rows[selectedSymbol?.scrip] || [];
        if (!currentRows.length) return;

        // Check if any Market order needs price update
        const needsUpdate = currentRows.some(row =>
            row.orderType !== "L" && symbolData[row.code]?.ltp && !row.price
        );

        if (!needsUpdate) return;

        // Create new array with updated prices for Market orders only
        const updatedRows = currentRows.map(row => {
            if(symbolData[row.code] && row.orderType !== "L"){
              return { ...row, price: symbolData[row.code]?.ltp };
            }
            return row;
        });

        setRows(prev => ({
            ...prev,
            [selectedSymbol?.scrip]: updatedRows,
        }));
    }, [symbolData, selectedSymbol]);

    const handleLotxChange = (newLotx, reset) => {
        if (newLotx === lotx) return;

        let updatedData = selectedData;

        if (newLotx !== 1 || reset == null) {
            updatedData = selectedData.map((obj) => {
                const initial_lot = obj.lotSize / lotx;
                const updated_lot = Math.round(initial_lot * newLotx);
                return { ...obj, lotSize: updated_lot };
            });
        }

        setRows(prev => ({
            ...prev,
            [selectedSymbol?.scrip]: updatedData,
        }));

        setLotxMap(prev => ({
            ...prev,
            [selectedSymbol?.scrip]: newLotx,
        }));
    };

    const handleMarketTypeChange = (newType) => {
        if (newType === marketType) return;
        setMarketTypeMap(prev => ({
            ...prev,
            [selectedSymbol?.scrip]: newType,
        }));
    };

    const clearBasket = () => {
        setRows(prev => ({
            ...prev,
            [selectedSymbol?.scrip]: []
        }));
        setMarketTypeMap(prev => ({
            ...prev,
            [selectedSymbol?.scrip]: "NRML",
        }));
        setLotxMap(prev => ({
            ...prev,
            [selectedSymbol?.scrip]: 1,
        }));
        setLimitOrderStates(prev => ({
            ...prev,
            [selectedSymbol?.scrip]: [],
        }));
        setSymbolsSelectedStrategy(prev => ({
            ...prev, [selectedSymbol.scrip]: null
        }));
    }



    return (
        <OptionChainScreenContext.Provider value={{
            selectedSymbol,
            setSelectedSymbol,
            searchSymbol,
            setSearchSymbol,
            allSymbols,
            setAllSymbols,
            allOptionsData,
            setAlloptionsData,
            currentOptionsData,
            setCurrentOptionsData,
            rows,
            setRows,
            selectedData,
            setSelectedData,
            selectedExpiry,
            setSelectedExpiry,
            symbolsActiveCategory,
            setSymbolsActiveCategory,
            symbolsSelectedStrategy,
            setSymbolsSelectedStrategy,
            limitOrderStates,
            setLimitOrderStates,
            lotx,
            setLotx,
            handleLotxChange,
            lotxMap, setLotxMap,
            marketTypeMap, setMarketTypeMap,
            marketType, setMarketType,
            handleMarketTypeChange,
            clearBasket,
            activeTab, setActiveTab,
            configureSettingsMap, setConfigureSettingsMap
        }}>
            {children}
        </OptionChainScreenContext.Provider>
    );
};
